import { UNKNOWN_LABEL } from '../LivePreview/liveMetricDisplay';

import { formatISO, getTimezoneAbbreviation } from '@/util/date';

/*
 * venueTime.ts — every First Responder console timestamp, rendered on the VENUE's clock.
 *
 * The wire carries UTC ISO 8601 instants (`…Z`) plus the job address's IANA timezone
 * (WireJobRow/WireJobDetail `timezone`). Printing the raw instant is unreadable, and printing it
 * on the BROWSER's clock is worse: an operator in one timezone would silently see a different
 * time from the venue's own staff for the same shift.
 *
 * So this module composes the same pair the rest of the admin already uses — `formatISO`
 * (src/util/date.ts, defaulting to America/New_York, never the browser zone) and the
 * `getTimezoneAbbreviation` badge text that Job Monitor and Timesheets render through <TZBadge>.
 * The abbreviation is appended inline rather than as a styled badge because these timestamps
 * appear inside running sentences ("Triggered Sep 6, 9:46 AM CDT"), not table cells.
 *
 * Two failure modes are absorbed here so no caller has to think about them:
 *   - a null or unparseable instant renders "Unknown" — never a fabricated or browser-local time;
 *   - a missing or invalid IANA name falls back to the shared Eastern default rather than
 *     throwing (Intl.DateTimeFormat raises a RangeError on an unknown zone).
 */

/** The SAME word the rest of the console uses for missing evidence, not a second copy of it. */
export const VENUE_TIME_UNKNOWN = UNKNOWN_LABEL;

/** Mirrors formatISO's own default. A venue with no readable zone is rendered Eastern, not local. */
const FALLBACK_TIMEZONE = 'America/New_York';

const PATTERNS = {
  datetime: 'MMM d, h:mm a',
  time: 'h:mm a',
  date: 'MMM d',
} as const;

export type VenueTimePattern = keyof typeof PATTERNS;

/**
 * `locale: true` skips getTimezoneAbbreviation's own "browser zone === venue zone" shortcut: the
 * abbreviation must be part of the sentence for every reader, including one who happens to sit in
 * the venue's timezone, because the sentence is also read in screenshots and exported reports.
 */
const render = (
  iso: string,
  timezone: string,
  pattern: VenueTimePattern,
): string => {
  const at = formatISO(iso, PATTERNS[pattern], timezone);
  const abbreviation = getTimezoneAbbreviation(timezone, iso, true);
  return abbreviation ? `${at} ${abbreviation}` : at;
};

export const formatVenueTime = (
  iso: string | null,
  timezone: string | null | undefined,
  pattern: VenueTimePattern,
): string => {
  if (!iso || Number.isNaN(Date.parse(iso))) return VENUE_TIME_UNKNOWN;
  try {
    return render(iso, timezone || FALLBACK_TIMEZONE, pattern);
  } catch {
    // An unknown IANA name reached us. Fall back rather than blanking the screen.
    return render(iso, FALLBACK_TIMEZONE, pattern);
  }
};

/**
 * A start/end pair for one line of copy. When both instants land on the same venue day the second
 * one drops its (identical) date, so the modal header reads
 * "Shift starts Sep 6, 6:31 PM CDT · First Responder acts 9:46 AM CDT". An Unknown half proves
 * nothing about the day, so nothing is elided against it.
 */
export const formatVenueRange = (
  startIso: string | null,
  endIso: string | null,
  timezone: string | null | undefined,
): [string, string] => {
  const start = formatVenueTime(startIso, timezone, 'datetime');
  const end = formatVenueTime(endIso, timezone, 'datetime');
  if (start === VENUE_TIME_UNKNOWN || end === VENUE_TIME_UNKNOWN) {
    return [start, end];
  }
  const sameDay =
    formatVenueTime(startIso, timezone, 'date') ===
    formatVenueTime(endIso, timezone, 'date');
  return [start, sameDay ? formatVenueTime(endIso, timezone, 'time') : end];
};
